'use client';

import { IT_INT } from '@slims/shared/constants';
import { DemoMeterDataView, type DemoMeterLayer, type DemoMeterTarget } from '@slims/ui/organisms';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useContextOrg } from '@/hooks/use-context-org';
import type { Meter } from '@/hooks/use-meters';
import { api } from '@/lib/utils/api';

export function DemoMeterData() {
	const org = useContextOrg();
	// Ogni configurazione ha una selezione indipendente: nessun id precedente
	// deve finire in una richiesta inviata nel nuovo ambito.
	return <ScopedDemoMeterData key={org?.orgId ?? ''} orgId={org?.orgId} enabled={!!org?.demo} />;
}

function ScopedDemoMeterData({ orgId, enabled }: { orgId?: string; enabled: boolean }) {
	const queryClient = useQueryClient();
	const [selectedId, setSelectedId] = useState('');
	const [busy, setBusy] = useState(false);
	const [result, setResult] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const targets = useQuery({
		queryKey: ['demo-meter-targets', orgId],
		enabled: !!orgId && enabled,
		queryFn: async (): Promise<DemoMeterTarget[]> => {
			const [meters, members] = await Promise.all([
				api.get<Meter[]>('/api/meters', { orgId }),
				api.get<{ id: string; nome: string }[]>('/api/members', { orgId }),
			]);
			const names = new Map(members.map((m) => [m.id, m.nome]));
			return meters
				.map((m) => {
					const kind =
						m.kind ?? (m.tipo === 'storage' ? 'M3' : m.tipo === 'injection' ? 'M2' : 'M1');
					return {
						id: m.id,
						kind,
						label: `${names.get(m.memberId) ?? 'Socio'} · ${kind} · ${m.nome || m.codice || 'Codice non assegnato'}`,
					};
				})
				.sort((a, b) => a.label.localeCompare(b.label, 'it'));
		},
	});
	const selected = targets.data?.find((m) => m.id === selectedId);

	async function generate(layer: DemoMeterLayer) {
		if (!enabled || !orgId || !selected || busy) return;
		setBusy(true);
		setError(null);
		setResult(null);
		try {
			const response = await api.post<{ results: { written: number }[] }>(
				`/api/meters/${selected.id}/demo-data?orgId=${orgId}`,
				{ layer },
			);
			const total = response.results.reduce((sum, r) => sum + r.written, 0);
			setResult(`Dati demo generati · ${total.toLocaleString('it-IT', IT_INT)} righe`);
			void queryClient.invalidateQueries({ queryKey: ['meter-hourly', orgId, selected.id] });
		} catch (e) {
			setError(e instanceof Error ? e.message : 'Generazione fallita. Riprova.');
		} finally {
			setBusy(false);
		}
	}

	return (
		<DemoMeterDataView
			enabled={enabled}
			loading={enabled && targets.isLoading}
			error={
				error ??
				(targets.error ? 'Impossibile caricare i punti di misura. Ricarica la pagina.' : null)
			}
			targets={targets.data ?? []}
			selectedId={selected?.id ?? ''}
			onSelect={(id) => {
				setSelectedId(id);
				setError(null);
				setResult(null);
			}}
			busy={busy}
			onGenerate={generate}
			result={result}
		/>
	);
}
